import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useUi, setUi } from "@/lib/uiConfig";

export const TikTokIcon = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 0 1-2.59 2.5c-1.42 0-2.6-1.16-2.6-2.6 0-1.72 1.66-3.01 3.37-2.48V9.66c-3.45-.46-6.47 2.22-6.47 5.64 0 3.33 2.76 5.7 5.69 5.7 3.14 0 5.69-2.55 5.69-5.7V9.01a7.35 7.35 0 0 0 4.3 1.38V7.3s-1.88.09-3.24-1.48z" />
  </svg>
);

const useLive = () => {
  const live = useUi()?.tiktok_live;
  const [, tick] = useState(0);
  useEffect(() => {
    if (!live?.ends_at) return undefined;
    const t = setInterval(() => tick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, [live?.ends_at]);
  return live && live.on && live.ends_at && new Date(live.ends_at) <= new Date() ? { ...live, on: false } : live;
};

const left = (ends) => {
  const m = Math.max(0, Math.ceil((new Date(ends) - Date.now()) / 60000));
  return m >= 60 ? `${Math.floor(m / 60)}:${String(m % 60).padStart(2, "0")} Std.` : `${m} Min.`;
};

export function TikTokLiveBar() {
  const live = useLive();
  if (!live?.on) return null;
  return (
    <a href={live.url} target="_blank" rel="noopener noreferrer" data-testid="tiktok-live-bar" className="tiktok-live-bar h-10 flex items-center justify-center gap-2 px-3 text-white text-[12px] sm:text-sm font-extrabold">
      <span className="relative flex w-2.5 h-2.5 shrink-0"><span className="absolute inset-0 rounded-full bg-red-500 animate-ping" /><span className="relative w-2.5 h-2.5 rounded-full bg-red-500" /></span>
      <span className="w-6 h-6 rounded-full bg-white text-black flex items-center justify-center shrink-0"><TikTokIcon className="w-3.5 h-3.5" /></span>
      <span className="truncate" data-testid="tiktok-live-text">Saskia ist jetzt live auf TikTok!</span>
      <span className="shrink-0 h-7 px-3 rounded-full bg-white text-rose-600 text-xs font-extrabold inline-flex items-center" data-testid="tiktok-live-button">Zum Live</span>
    </a>
  );
}

export const TikTokLiveSpacer = () => (useLive()?.on ? <div className="h-10" /> : null);

export function TikTokLiveToggle() {
  const live = useLive();
  const [busy, setBusy] = useState(false);
  const on = !!live?.on;
  const toggle = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/tiktok-live", { on: !on });
      setUi({ tiktok_live: data });
      toast.success(data.on ? "TikTok-Live eingeblendet · Push an alle wird verschickt" : "TikTok-Live ausgeblendet");
    } catch { toast.error("Hat nicht geklappt – bitte nochmal versuchen"); }
    setBusy(false);
  };
  return (
    <button type="button" onClick={toggle} disabled={busy || !live} data-testid="tiktok-live-toggle" className={`inline-flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 min-h-10 py-2 px-4 rounded-full text-sm font-extrabold shadow-sm transition-colors ${on ? "bg-slate-900 text-white" : "bg-black text-white hover:bg-slate-800"}`}>
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <TikTokIcon />}
      {on ? "TikTok-Live beenden" : "Ich bin live auf TikTok"}
      {on && <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />}
      {on && live.ends_at && <span className="text-[11px] font-semibold text-white/70" data-testid="tiktok-live-remaining">· endet automatisch in {left(live.ends_at)}</span>}
    </button>
  );
}
